import React from 'react'
import { Card } from '../ui/Card'
import { WidgetHeader } from './WidgetKommende'
import { t } from '../../lib/i18n'

export interface CompanyKpi {
  company_id: string
  name: string
  cvr: string | null
  lease_count: number
  occupied_count: number
  occupancy_pct: number
  collected_oere: number
  arrears_oere: number
}

export interface CompanyKpiTotal {
  lease_count: number
  occupied_count: number
  occupancy_pct: number
  collected_oere: number
  arrears_oere: number
}

export interface CompanyKpisResponse {
  companies: CompanyKpi[]
  total: CompanyKpiTotal
}

interface WidgetSelskaberProps {
  data: CompanyKpisResponse | null
  loading: boolean
  editMode?: boolean
}

function formatDKK(oere: number): string {
  return new Intl.NumberFormat('da-DK', {
    style: 'currency', currency: 'DKK', maximumFractionDigits: 0,
  }).format(oere / 100)
}

const ALL = 'all'

const thStyle: React.CSSProperties = {
  padding: '8px 16px', fontSize: 11, fontWeight: 600, color: '#6b7280',
  textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: '1px solid #e8eaed', whiteSpace: 'nowrap',
}
const tdStyle: React.CSSProperties = { padding: '10px 16px', whiteSpace: 'nowrap' }

export function WidgetSelskaber({ data, loading, editMode }: WidgetSelskaberProps) {
  const s = t().dashboard.companies
  const [selected, setSelected] = React.useState<string>(ALL)

  const companies = data?.companies ?? []
  const filtered = selected === ALL ? companies : companies.filter((c) => c.company_id === selected)
  // Fall back to all if the selected company vanished from the list.
  const rows = filtered.length > 0 ? filtered : companies
  const isAll = selected === ALL || filtered.length === 0
  const total: CompanyKpiTotal | null = isAll
    ? data?.total ?? null
    : rows[0]
      ? {
          lease_count: rows[0].lease_count,
          occupied_count: rows[0].occupied_count,
          occupancy_pct: rows[0].occupancy_pct,
          collected_oere: rows[0].collected_oere,
          arrears_oere: rows[0].arrears_oere,
        }
      : null

  const arrearsColor = (oere: number) => (oere > 0 ? '#dc2626' : '#111827')

  return (
    <Card
      padding="none"
      style={editMode ? { border: '1.5px dashed #4f46e5', background: '#fafbff' } : undefined}
    >
      <WidgetHeader title={s.title} />
      {loading ? (
        <div style={{ padding: '10px 16px', color: '#94a3b8', fontSize: 12 }}>{t().dashboard.loading}</div>
      ) : !data || companies.length === 0 || !total ? (
        <div style={{ padding: '10px 16px', color: '#94a3b8', fontSize: 12 }}>{s.noData}</div>
      ) : (
        <>
          <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <label htmlFor="dashboard-company-filter" style={{ fontSize: 12, color: '#64748b' }}>
              {s.filterLabel}
            </label>
            <select
              id="dashboard-company-filter"
              value={isAll ? ALL : selected}
              onChange={(e) => setSelected(e.target.value)}
              style={{
                fontSize: 13, padding: '6px 10px', borderRadius: 8,
                border: '1px solid #e2e8f0', background: '#fff', color: '#0f172a',
                maxWidth: '100%',
              }}
            >
              <option value={ALL}>{s.allCompanies}</option>
              {companies.map((c) => (
                <option key={c.company_id} value={c.company_id}>
                  {c.cvr ? `${c.name} (${c.cvr})` : c.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ ...thStyle, textAlign: 'left' }}>{s.colCompany}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{s.colLeases}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{s.colOccupancy}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{s.colCollected}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{s.colArrears}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.company_id} style={{ borderBottom: '1px solid #e8eaed' }}>
                    <td style={{ ...tdStyle, fontWeight: 500, color: '#111827' }}>{c.name}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#111827' }}>{c.lease_count}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#111827' }}>{c.occupancy_pct} %</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#111827' }}>{formatDKK(c.collected_oere)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: arrearsColor(c.arrears_oere) }}>
                      {formatDKK(c.arrears_oere)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f9fafb' }}>
                  <td style={{ ...tdStyle, fontWeight: 700, color: '#111827' }}>{s.total}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#111827' }}>{total.lease_count}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#111827' }}>{total.occupancy_pct} %</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#111827' }}>{formatDKK(total.collected_oere)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: arrearsColor(total.arrears_oere) }}>
                    {formatDKK(total.arrears_oere)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </Card>
  )
}
